import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef } from "react";

// Signature field for finger, pen or mouse. Strokes are kept as points so the drawing survives
// a resize or a phone rotation; toDataURL() returns the signature cropped to its ink as a PNG.
const INK = "#10243a";
const MAX_EXPORT_WIDTH = 900;
const PADDING = 12;

const SignaturePad = forwardRef(({ onChange, ariaLabel }, ref) => {
  const canvas = useRef(null);
  const strokes = useRef([]);
  const drawing = useRef(null);

  const lineWidthFor = pointerType => (pointerType === "pen" ? 2.2 : 2.6);

  const drawStroke = (ctx, stroke) => {
    const points = stroke.points;
    if (!points.length) return;
    ctx.strokeStyle = INK;
    ctx.fillStyle = INK;
    ctx.lineWidth = stroke.width;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    if (points.length === 1) {
      ctx.beginPath();
      ctx.arc(points[0].x, points[0].y, stroke.width / 2, 0, Math.PI * 2);
      ctx.fill();
      return;
    }
    ctx.beginPath();
    ctx.moveTo(points[0].x, points[0].y);
    // Quadratic curves through the midpoints: smooth without lagging behind the finger
    for (let i = 1; i < points.length - 1; i += 1) {
      const mid = {
        x: (points[i].x + points[i + 1].x) / 2,
        y: (points[i].y + points[i + 1].y) / 2
      };
      ctx.quadraticCurveTo(points[i].x, points[i].y, mid.x, mid.y);
    }
    const last = points[points.length - 1];
    ctx.lineTo(last.x, last.y);
    ctx.stroke();
  };

  const redraw = useCallback(() => {
    const el = canvas.current;
    if (!el) return;
    const ctx = el.getContext("2d");
    const ratio = window.devicePixelRatio || 1;
    ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
    ctx.clearRect(0, 0, el.width, el.height);
    strokes.current.forEach(stroke => drawStroke(ctx, stroke));
  }, []);

  const fit = useCallback(() => {
    const el = canvas.current;
    if (!el) return;
    const ratio = window.devicePixelRatio || 1;
    const { width, height } = el.getBoundingClientRect();
    if (!width || !height) return;
    const previous = el.dataset.size ? JSON.parse(el.dataset.size) : null;
    // Keep the drawing in proportion when the box changes size
    if (previous && previous.width && (previous.width !== width || previous.height !== height)) {
      const sx = width / previous.width;
      const sy = height / previous.height;
      strokes.current = strokes.current.map(s => ({
        ...s,
        points: s.points.map(p => ({ x: p.x * sx, y: p.y * sy }))
      }));
    }
    el.dataset.size = JSON.stringify({ width, height });
    el.width = Math.round(width * ratio);
    el.height = Math.round(height * ratio);
    redraw();
  }, [redraw]);

  useEffect(() => {
    fit();
    const observer = typeof ResizeObserver !== "undefined" ? new ResizeObserver(fit) : null;
    if (observer && canvas.current) observer.observe(canvas.current);
    window.addEventListener("orientationchange", fit);
    return () => {
      if (observer) observer.disconnect();
      window.removeEventListener("orientationchange", fit);
    };
  }, [fit]);

  const pointOf = event => {
    const rect = canvas.current.getBoundingClientRect();
    return { x: event.clientX - rect.left, y: event.clientY - rect.top };
  };

  const onPointerDown = event => {
    if (event.button !== undefined && event.button > 0) return;
    event.preventDefault();
    canvas.current.setPointerCapture?.(event.pointerId);
    drawing.current = { width: lineWidthFor(event.pointerType), points: [pointOf(event)] };
    strokes.current.push(drawing.current);
    redraw();
  };

  const onPointerMove = event => {
    if (!drawing.current) return;
    event.preventDefault();
    const events = event.nativeEvent.getCoalescedEvents?.() || [event];
    events.forEach(e => drawing.current.points.push(pointOf(e)));
    redraw();
  };

  const onPointerUp = () => {
    if (!drawing.current) return;
    drawing.current = null;
    onChange?.(strokes.current.length > 0);
  };

  useImperativeHandle(ref, () => ({
    clear: () => {
      strokes.current = [];
      redraw();
      onChange?.(false);
    },
    // A tap or two is not a signature
    isEmpty: () => strokes.current.reduce((n, s) => n + s.points.length, 0) < 5,
    /** PNG data URL cropped to the ink with a small margin, at most 900 px wide. */
    toDataURL: () => {
      const all = strokes.current.flatMap(s => s.points);
      if (!all.length) return "";
      const minX = Math.min(...all.map(p => p.x)) - PADDING;
      const minY = Math.min(...all.map(p => p.y)) - PADDING;
      const maxX = Math.max(...all.map(p => p.x)) + PADDING;
      const maxY = Math.max(...all.map(p => p.y)) + PADDING;
      const width = Math.max(1, maxX - minX);
      const height = Math.max(1, maxY - minY);
      const scale = Math.min(2, MAX_EXPORT_WIDTH / width);
      const out = document.createElement("canvas");
      out.width = Math.round(width * scale);
      out.height = Math.round(height * scale);
      const ctx = out.getContext("2d");
      ctx.setTransform(scale, 0, 0, scale, -minX * scale, -minY * scale);
      strokes.current.forEach(stroke => drawStroke(ctx, stroke));
      return out.toDataURL("image/png");
    }
  }));

  return (
    <canvas
      ref={canvas}
      className="wr-signature-canvas"
      role="img"
      aria-label={ariaLabel}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      onPointerLeave={onPointerUp}
    />
  );
});

SignaturePad.displayName = "SignaturePad";

export default SignaturePad;
